import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Link,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider,
} from '@mui/material';
import DocumentsAPI from '../../../services/api/ai/project-docs/documents-api';
import Logger from '../../../utils/logger';
import {
  SUPPORTED_EXTENSIONS,
  UNSUPPORTED_TYPE_MESSAGE,
  fileExtension,
} from './document-file-types';

export const RETENTION_NOTE =
  'FieldTime keeps the three most recent versions for history and comparison. ACC remains the long-term version record.';
export const NO_LONGER_KEPT_MESSAGE =
  'This version is no longer kept in FieldTime. ACC remains the long-term version record.';
export const TEXT_ONLY_NOTE =
  'Text comparison only. Drawing graphics, markups, and layout are not compared.';

const CHANGE_LABELS = {
  added: 'Added',
  removed: 'Removed',
  changed: 'Changed',
};

// A version id that no longer resolves (pruned by retention since this list
// loaded) is a 404 from both the file and compare endpoints.
const versionErrorMessage = (err, fallback) =>
  err.response?.status === 404
    ? NO_LONGER_KEPT_MESSAGE
    : err.response?.data?.message || fallback;

const pageLabel = ({ basePage, targetPage }) => {
  if (basePage && targetPage && basePage !== targetPage) {
    return `Page ${basePage} → ${targetPage}`;
  }
  const page = targetPage || basePage;
  return page ? `Page ${page}` : null;
};

const SegmentText = ({ label, text }) =>
  text === null ? null : (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ mr: 0.5 }}
      >
        {label}:
      </Typography>
      {text}
    </Typography>
  );

const ComparisonResult = ({ comparison }) => {
  const { base, target, identicalContent, segments, truncated } = comparison;
  const baseLabel = `Version ${base.versionNumber}`;
  const targetLabel = `Version ${target.versionNumber}`;
  const count = (change) => segments.filter((s) => s.change === change).length;

  return (
    <Box data-testid="version-comparison">
      <Typography variant="subtitle2">
        {baseLabel} → {targetLabel}
      </Typography>
      {identicalContent ? (
        <Typography variant="body2">
          No changes: these versions have identical content.
        </Typography>
      ) : segments.length === 0 ? (
        <Typography variant="body2">No text changes found.</Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            {count('added')} added · {count('removed')} removed ·{' '}
            {count('changed')} changed
          </Typography>
          {segments.map((segment, index) => (
            <Box key={index} sx={{ py: 0.75 }}>
              {index > 0 && <Divider sx={{ mb: 0.75 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={CHANGE_LABELS[segment.change]}
                  sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                />
                {pageLabel(segment) && (
                  <Typography variant="caption" color="text.secondary">
                    {pageLabel(segment)}
                  </Typography>
                )}
              </Box>
              <SegmentText label={baseLabel} text={segment.baseText} />
              <SegmentText label={targetLabel} text={segment.targetText} />
            </Box>
          ))}
        </>
      )}
      {truncated && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          This comparison was truncated: only the first changes are shown.
        </Typography>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.5 }}
      >
        {TEXT_ONLY_NOTE}
      </Typography>
    </Box>
  );
};

/**
 * Version History for one document: the retained FieldTime versions (at
 * most three, newest first), opening any of them, Upload New Version
 * (manual-upload documents only), and a text comparison of any two.
 *
 * Unlike the page's other dialogs this one owns its own requests: it is
 * mounted per document (the page keys it by document id) and unmounted on
 * close or project switch, so `activeRef` is the whole staleness guard.
 * Opening a file goes through the page's onOpenVersion, which reuses the
 * existing authenticated file fetch and rejects on failure so the error can
 * be shown here. onVersionUploaded lets the page refresh its document list.
 */
const DocumentVersionHistoryDialog = ({
  projectId,
  document,
  token,
  onOpenVersion,
  onVersionUploaded,
  onClose,
}) => {
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [uploading, setUploading] = useState(false);
  const [baseVersionId, setBaseVersionId] = useState('');
  const [targetVersionId, setTargetVersionId] = useState('');
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await DocumentsAPI.listDocumentVersions(
        projectId,
        document.id,
        token
      );
      if (!activeRef.current) return;
      setVersions(data);
      // Default comparison: the previous version -> the current one.
      setTargetVersionId(data[0]?.id || '');
      setBaseVersionId(data[1]?.id || '');
      setComparison(null);
    } catch (err) {
      if (!activeRef.current) return;
      Logger.error('Error loading document versions', err);
      setLoadError(
        err.response?.data?.message || 'Failed to load version history.'
      );
    } finally {
      if (activeRef.current) setLoading(false);
    }
  }, [projectId, document.id, token]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  const handleOpen = async (version) => {
    setActionError('');
    try {
      await onOpenVersion(document.id, version.id);
    } catch (err) {
      if (!activeRef.current) return;
      Logger.error('Error opening document version', err);
      setActionError(versionErrorMessage(err, 'Failed to open this version.'));
    }
  };

  const handleFileSelected = async (file) => {
    if (!file) return;
    setActionError('');
    setNotice('');
    if (!SUPPORTED_EXTENSIONS.includes(fileExtension(file.name))) {
      setActionError(UNSUPPORTED_TYPE_MESSAGE);
      return;
    }
    setUploading(true);
    try {
      const version = await DocumentsAPI.uploadDocumentVersion(
        projectId,
        document.id,
        file,
        token
      );
      if (!activeRef.current) return;
      Logger.userAction('Uploaded Project Documents version', {
        projectId,
        documentId: document.id,
      });
      setNotice(
        `Version ${version.versionNumber} uploaded and is now current.`
      );
      onVersionUploaded();
      await loadVersions();
    } catch (err) {
      if (!activeRef.current) return;
      Logger.error('Error uploading document version', err);
      setActionError(
        err.response?.data?.message || 'Failed to upload the new version.'
      );
    } finally {
      if (activeRef.current) setUploading(false);
    }
  };

  const handleCompare = async () => {
    setActionError('');
    setComparison(null);
    setComparing(true);
    try {
      const result = await DocumentsAPI.compareDocumentVersions(
        projectId,
        document.id,
        baseVersionId,
        targetVersionId,
        token
      );
      if (activeRef.current) setComparison(result);
    } catch (err) {
      if (!activeRef.current) return;
      Logger.error('Error comparing document versions', err);
      setActionError(
        versionErrorMessage(err, 'Failed to compare these versions.')
      );
    } finally {
      if (activeRef.current) setComparing(false);
    }
  };

  const selectVersion = (setter) => (e) => {
    setter(e.target.value);
    setComparison(null);
  };

  const busy = uploading || comparing;
  const canUploadVersion = document.source === 'manual_upload';
  const sameVersionSelected = baseVersionId === targetVersionId;

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Version history</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Document: {document.title || document.file_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {RETENTION_NOTE}
            </Typography>
          </Box>

          {actionError && <Alert severity="error">{actionError}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {!loading && loadError && <Alert severity="error">{loadError}</Alert>}

          {!loading && versions && (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {versions.map((version) => (
                <Box
                  component="li"
                  key={version.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Version {version.versionNumber}
                  </Typography>
                  {version.isCurrentVersion && (
                    <Chip
                      size="small"
                      color="primary"
                      label="Current"
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    title={version.fileName}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {version.fileName} ·{' '}
                    {new Date(version.createdAt).toLocaleDateString()}
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => handleOpen(version)}
                    aria-label={`Open version ${version.versionNumber}`}
                  >
                    Open
                  </Link>
                </Box>
              ))}
            </Box>
          )}

          {!loading && versions && versions.length >= 2 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Compare versions
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl
                  size="small"
                  sx={{ minWidth: 130 }}
                  disabled={busy}
                >
                  <InputLabel id="compare-base-version-label">From</InputLabel>
                  <Select
                    labelId="compare-base-version-label"
                    label="From"
                    value={baseVersionId}
                    onChange={selectVersion(setBaseVersionId)}
                  >
                    {versions.map((version) => (
                      <MenuItem key={version.id} value={version.id}>
                        Version {version.versionNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  sx={{ minWidth: 130 }}
                  disabled={busy}
                >
                  <InputLabel id="compare-target-version-label">To</InputLabel>
                  <Select
                    labelId="compare-target-version-label"
                    label="To"
                    value={targetVersionId}
                    onChange={selectVersion(setTargetVersionId)}
                  >
                    {versions.map((version) => (
                      <MenuItem key={version.id} value={version.id}>
                        Version {version.versionNumber}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCompare}
                  disabled={busy || sameVersionSelected}
                  startIcon={comparing ? <CircularProgress size={14} /> : null}
                >
                  {comparing ? 'Comparing...' : 'Compare'}
                </Button>
              </Box>
              {sameVersionSelected && (
                <Typography variant="caption" color="text.secondary">
                  Choose two different versions to compare.
                </Typography>
              )}
            </Box>
          )}

          {comparison && <ComparisonResult comparison={comparison} />}
        </Box>
      </DialogContent>
      <DialogActions>
        {canUploadVersion && (
          <Button
            component="label"
            disabled={busy || loading}
            startIcon={uploading ? <CircularProgress size={16} /> : null}
          >
            {uploading ? 'Uploading...' : 'Upload new version'}
            <input
              type="file"
              hidden
              accept={SUPPORTED_EXTENSIONS.join(',')}
              disabled={busy || loading}
              onChange={(e) => {
                handleFileSelected(e.target.files?.[0]);
                // Allow re-selecting the same file after a failure.
                e.target.value = '';
              }}
            />
          </Button>
        )}
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentVersionHistoryDialog;
